/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const { Parser } = require('@cedalo/parser');
const JSG = require('../../JSG');
const CellRange = require('../model/CellRange');
const GraphParserContext = require('./GraphParserContext');

const isFunction = (type) => type === 'function' || type === 'condition';
const isValidBrackets = (brackets) => brackets && (brackets.open !== -1 || brackets.close !== -1);
const getFunctionName = ({ type, value }) => (type === 'condition' ? 'IF' : type === 'function' ? value : undefined);
const tokenAt = (infos, pos) =>
	infos.reduce((token, info) => {
		// ignore info with no length or use it if it is next parameter
		if (pos >= info.start && pos <= info.end && (info.end > info.start || token.paramIndex == null)) {
			return info;
		}
		return token;
	}, {});

const getCell = ({ sheetName, reference }) => {
	const sheet = JSG.findSheetModel(sheetName);
	const rc = sheet && CellRange.refToRC(reference, sheet);
	return rc ? sheet.cells.getRC(rc.column - sheet.colOffset, rc.row) : undefined;
};
const getCellReference = (sheet, reference) => {
	const sheetref = reference.split('!');
	return sheetref.length === 2 ? { sheetName: sheetref[0], reference: sheetref[1] } : { sheetName: sheet.name, reference };
};
const resolveCellReferences = (infos, sheet) => {
	infos.forEach((info) => {
		const { type, value } = info;
		if (type === 'identifier') {
			const cellref = getCellReference(sheet, value);
			const cell = getCell(cellref);
			if (cell) {
				// always work with string values:
				info.value = `${cell.value}`;
				info.type = 'string';
				info.cell = cellref;
			}
		}
	});
};
const infoForToken = (token, pos, info = {}) => {
	if (token) {
		const { brackets, end, isInvalid, paramIndex, parent, start, type, value } = token;
		const isNoFunction = !isFunction(type);
		if (info.paramIndex == null) {
			info.paramIndex = isNoFunction || pos === start || pos >= end ? paramIndex : undefined;
			if (info.paramIndex != null) {
				info.paramEnd = end;
				info.paramStart = start;
				info.paramType = type;
				info.paramValue = value;
			}
		}
		info.function = (pos > start && pos < end) || isInvalid ? getFunctionName(token) : undefined;
		if (info.function == null) {
			if (info.brackets == null && isNoFunction && isValidBrackets(brackets)) info.brackets = brackets;
			infoForToken(parent, pos, info);
		} else {
			info.brackets = brackets;
			if (info.paramIndex == null && pos > brackets.open && pos <= brackets.close) info.paramIndex = 0;
		}
		info.type = type;
	}
	return info;
};
const infoAtPosition = (infos, pos) => {
	const token = tokenAt(infos, pos);
	const info = infoForToken(token, pos);
	if (token.parent && token.parent.type === 'binaryop' && token.parent.value === '.') {
		let index = infos.indexOf(token);
		if (index !== -1) {
			info.dotIdentifiers = [];
			while (index >= 0) {
				const dotInfo = infos[index];
				if (dotInfo) {
					if (dotInfo.type === 'identifier' ||
						dotInfo.type === 'string' ||
						dotInfo.type === 'number') {
						info.dotIdentifiers.push(dotInfo.value);
						if (dotInfo.cell && dotInfo.value === '{ JSON }') {
							info.sourceCell = dotInfo.cell;
						}
					} else if (dotInfo.type === 'undef') {
						info.dotIdentifiers.push('*');
						info.identifier = '*';
						info.start = token.start;
					}
				}
				index -= 1;
			}
			// console.log(info.dotIdentifiers);
		}
	}
	switch (token.type) {
	case 'identifier':
		info.start = token.start;
		info.identifier = token.value;
		break;
	case 'string':
	case 'number':
		info.start = token.start;
		if (token.end - token.start === token.value.length + 1) {
			// single quotation is not visible here
			info.string = `"${token.value}`;
		} else {
			info.string = token.value;
		}
		break;
	case 'undef':
		info.start = pos;
		break;
	default:
	}

	return info;
};

/**
 * Used by framework.
 * Note: Should not be instantiated! An instance is accessible via <code>JSG.FormulaParser</code>.
 */
class GraphParser {
	constructor() {
		this._context = new GraphParserContext();
	}

	get context() {
		return this._context;
	}

	set context(ctxt) {
		this._context = ctxt;
	}

	runIgnoringErrors(fn, doIt = true) {
		const { ignoreErrors } = this._context;
		this._context.ignoreErrors = doIt;
		const result = fn();
		this._context.ignoreErrors = ignoreErrors;
		return result;
	}

	parseSheet(formula, sheet) {
		this._context.setScope({ sheet });
		const term = Parser.parse(formula, this._context);
		// clear context to prevent memory leak since graph or item are stored globally...
		this._context.setScope(undefined);
		return term;
	}

	parse(formula, graph, item, sheet) {
		this._context.setScope({ graph, item, sheet });
		const term = Parser.parse(formula, this._context);
		// clear context to prevent memory leak since graph or item are stored globally...
		this._context.setScope(undefined);
		return term;
	}

	parseValue(str, sheet) {
		this._context.setScope({ sheet });
		const term = Parser.parseValue(str, this._context);
		// clear context to prevent memory leak since graph or item are stored globally...
		this._context.setScope(undefined);
		return term;
	}

	parseFormulaInfo(str, offset, sheet) {
		this._context.setScope({ sheet });
		const info = Parser.getFormulaInfos(str, this._context);
		if (offset == null || offset < 0 || offset > str.length) offset = 0;
		// clear context to prevent memory leak since graph or item are stored globally...
		this._context.setScope(undefined);
		if (sheet) resolveCellReferences(info, sheet);
		return infoAtPosition(info, offset, sheet);
	}
}

module.exports = GraphParser;
